import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Logo from '../components/Logo';
import SearchBar from '../components/SearchBar';
import ServiceCard from '../components/ServiceCard';
import CategoryStrip, { CATEGORIES } from '../components/CategoryPill';
import useAuthStore from '../store/useAuthStore';
import api from '../services/api';
import usePullToRefresh from '../hooks/usePullToRefresh';
import ProviderChecklist from '../components/ProviderChecklist';

export default function HomePage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [featured, setFeatured] = useState([]);
  const [studios, setStudios] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [featRes, stuRes] = await Promise.all([
        api.get('/services/featured'),
        api.get('/services?listingType=STUDIO&limit=6')
      ]);
      setFeatured(featRes.data);
      setStudios(stuRes.data.services || []);
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const { pulling, pullDistance } = usePullToRefresh(refresh);

  const greeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <div className="min-h-screen bg-dark-bg pb-24">
      {/* Pull to refresh indicator */}
      {pullDistance > 0 && (
        <div
          className="flex items-center justify-center overflow-hidden transition-all duration-150"
          style={{ height: Math.min(pullDistance * 0.6, 56) }}
        >
          <div className="flex items-center gap-2">
            {pulling ? (
              <>
                <div className="w-4 h-4 border-2 border-brand-purple border-t-transparent rounded-full animate-spin" />
                <span className="text-xs text-brand-purple font-medium">Release to refresh</span>
              </>
            ) : (
              <span className="text-xs text-dark-subtext">Pull to refresh</span>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="px-4 pt-6 pb-4">
        {/* Centered logo — same scale as Fiverr wordmark */}
        <div className="flex justify-center mb-3">
          <Logo size="md" showWordmark={true} centered={true} />
        </div>

        {/* Search bar */}
        <SearchBar />
      </div>

      {/* Provider checklist */}
      {user?.accountType === 'PROVIDER' && <ProviderChecklist />}

      {/* Categories */}
      <div className="px-4 mb-6">
        <CategoryStrip
          selected={null}
          onSelect={(cat) => navigate(`/explore${cat ? `?category=${cat}` : ''}`)}
        />
      </div>

      {/* Featured Services */}
      <section className="mb-8">
        <div className="flex items-center justify-between px-4 mb-3">
          <h2 className="text-lg font-bold text-dark-text">Featured Services</h2>
          <button onClick={() => navigate('/explore')} className="text-brand-purple text-sm font-medium">See all</button>
        </div>
        {loading ? (
          <div className="px-4 grid grid-cols-2 gap-3">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-48 bg-dark-surface rounded-2xl animate-pulse" />
            ))}
          </div>
        ) : (
          <div className="px-4 grid grid-cols-2 gap-3">
            {featured.slice(0, 6).map(s => <ServiceCard key={s.id} service={s} />)}
          </div>
        )}
      </section>

      {/* Studio Rentals */}
      <section className="mb-8">
        <div className="flex items-center justify-between px-4 mb-3">
          <h2 className="text-lg font-bold text-dark-text">Studio Rentals 🎙️</h2>
          <button onClick={() => navigate('/explore?category=STUDIO_RENTALS')} className="text-brand-purple text-sm font-medium">See all</button>
        </div>
        {studios.length === 0 && !loading ? (
          <div className="px-4">
            <div className="bg-dark-surface border border-dark-border rounded-2xl p-6 text-center">
              <p className="text-2xl mb-2">🎙️</p>
              <p className="text-dark-subtext text-sm">No studios listed yet. Check back soon!</p>
            </div>
          </div>
        ) : (
          <div className="px-4 grid grid-cols-2 gap-3">
            {studios.map(s => <ServiceCard key={s.id} service={s} />)}
          </div>
        )}
      </section>

      {/* Category cards */}
      <section className="px-4 mb-8">
        <h2 className="text-lg font-bold text-dark-text mb-3">Browse by Category</h2>
        <div className="grid grid-cols-3 gap-3">
          {CATEGORIES.map(cat => (
            <button
              key={cat.id}
              onClick={() => navigate(`/explore?category=${cat.id}`)}
              className="bg-dark-surface border border-dark-border rounded-2xl p-3 flex flex-col items-center gap-1 hover:border-brand-purple transition-colors"
            >
              <span className="text-2xl">{cat.emoji}</span>
              <span className="text-xs text-dark-subtext text-center font-medium leading-tight">{cat.label}</span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}
